import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

// Manages interactive uploading of photos, documents, and videos for assets.
// Supports drag-and-drop, tabbed category selection, live previews with removal, and submission progress.
export default class extends Controller {
  static targets = [
    "modal",
    "overlay",
    "dropzone",
    "fileInput",
    "tabBtn",
    "tabPanel",
    "previewContainer",
    "submitBtn",
    "submitBtnText",
    "submitSpinner",
    "emptyNotice",
    "typeHint",
    "categoryBadge"
  ]

  static values = {
    activeCategory: { type: String, default: "photos" } // "photos" | "documents" | "videos"
  }

  connect() {
    this.keyHandler = this.handleKey.bind(this)
    document.addEventListener("keydown", this.keyHandler)

    // Per-category DataTransfer store for staged files
    this.filesMap = {
      photos: new DataTransfer(),
      documents: new DataTransfer(),
      videos: new DataTransfer()
    }

    this.updateCategoryUI(this.activeCategoryValue)
  }

  disconnect() {
    document.removeEventListener("keydown", this.keyHandler)
    this.revokeObjectUrls()
  }

  handleKey(event) {
    if (event.key === "Escape" && this.hasModalTarget && !this.modalTarget.classList.contains("hidden")) {
      this.closeModal()
    }
  }

  open(event) {
    if (event) {
      event.preventDefault()
      const category = event.currentTarget.dataset.category || event.currentTarget.dataset.type
      if (category && ["photos", "documents", "videos"].includes(category)) {
        this.selectCategory(category)
      }
    }

    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("hidden")
      document.body.classList.add("overflow-hidden")
    }
  }

  closeModal(event) {
    if (event) event.preventDefault()
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden")
      document.body.classList.remove("overflow-hidden")
    }
  }

  backdropClick(event) {
    if (event.target === event.currentTarget) {
      this.closeModal()
    }
  }

  switchTab(event) {
    event.preventDefault()
    const category = event.currentTarget.dataset.category
    if (category) {
      this.selectCategory(category)
    }
  }

  selectCategory(category) {
    this.activeCategoryValue = category
    this.updateCategoryUI(category)
  }

  updateCategoryUI(category) {
    // Update tab button styles
    this.tabBtnTargets.forEach(btn => {
      const btnCat = btn.dataset.category
      if (btnCat === category) {
        btn.className = "inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 border-primary text-primary bg-primary/5 transition-colors"
      } else {
        btn.className = "inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors"
      }
    })

    // Update hints & file accept rules
    if (this.hasTypeHintTarget) {
      if (category === "photos") {
        this.typeHintTarget.textContent = t('javascript.controllers.asset_media_upload_controller.accepted_jpg_png_gif_webp_heic_images')
      } else if (category === "videos") {
        this.typeHintTarget.textContent = t('javascript.controllers.asset_media_upload_controller.accepted_mp4_mov_webm_avi_mkv_videos')
      } else {
        this.typeHintTarget.textContent = t('javascript.controllers.asset_media_upload_controller.accepted_pdf_doc_docx_xls_xlsx_txt_csv_zip_documen')
      }
    }

    // Refresh preview list
    this.renderPreviews()
  }

  // Trigger browser file picker for current category
  browseFiles(event) {
    if (event) event.preventDefault()
    const input = this.getCurrentInput()
    if (input) input.click()
  }

  // File input change event
  handleFileSelect(event) {
    const input = event.target
    const newFiles = Array.from(input.files || [])
    if (newFiles.length === 0) return

    const category = input.dataset.category || this.activeCategoryValue
    const dt = this.filesMap[category]

    newFiles.forEach(file => {
      // Avoid duplicate by name + size in the current batch
      const alreadyExists = Array.from(dt.files).some(
        f => f.name === file.name && f.size === file.size
      )
      if (!alreadyExists) {
        dt.items.add(file)
      }
    })

    // Sync input.files with DataTransfer
    input.files = dt.files
    this.renderPreviews()
  }

  // Drag & drop handlers
  dragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add("border-primary", "bg-primary/5")
    }
  }

  dragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("border-primary", "bg-primary/5")
    }
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("border-primary", "bg-primary/5")
    }

    const droppedFiles = Array.from(event.dataTransfer?.files || [])
    if (droppedFiles.length === 0) return

    droppedFiles.forEach(file => {
      const detectedCat = this.detectCategory(file) || this.activeCategoryValue
      const dt = this.filesMap[detectedCat]
      const alreadyExists = Array.from(dt.files).some(
        f => f.name === file.name && f.size === file.size
      )
      if (!alreadyExists) {
        dt.items.add(file)
      }

      // Sync into the corresponding input
      const input = this.fileInputTargets.find(i => i.dataset.category === detectedCat)
      if (input) {
        input.files = dt.files
      }
    })

    this.renderPreviews()
  }

  detectCategory(file) {
    const type = (file.type || "").toLowerCase()
    const name = (file.name || "").toLowerCase()

    if (type.startsWith("image/") || /\.(jpe?g|png|gif|webp|heic|svg)$/i.test(name)) {
      return "photos"
    }
    if (type.startsWith("video/") || /\.(mp4|mov|webm|avi|mkv|m4v)$/i.test(name)) {
      return "videos"
    }
    return "documents"
  }

  removeStagedFile(event) {
    event.preventDefault()
    const category = event.currentTarget.dataset.category
    const index = parseInt(event.currentTarget.dataset.index, 10)

    const dt = this.filesMap[category]
    if (!dt) return

    const newDt = new DataTransfer()
    Array.from(dt.files).forEach((file, i) => {
      if (i !== index) {
        newDt.items.add(file)
      }
    })

    this.filesMap[category] = newDt

    const input = this.fileInputTargets.find(i => i.dataset.category === category)
    if (input) {
      input.files = newDt.files
    }

    this.renderPreviews()
  }

  renderPreviews() {
    if (!this.hasPreviewContainerTarget) return

    this.revokeObjectUrls()
    this.objectUrls = []

    const allCategories = ["photos", "documents", "videos"]
    let totalCount = 0
    let html = ""

    allCategories.forEach(cat => {
      const dt = this.filesMap[cat]
      const files = Array.from(dt?.files || [])
      totalCount += files.length

      if (files.length > 0) {
        // cat is "photos" | "documents" | "videos" (identifiers); translate the label only
        const catTitle = t(`javascript.controllers.asset_media_upload_controller.categories.${cat}`)
        const catBadgeColor = cat === "photos" ? "bg-amber-100 text-amber-800" : (cat === "videos" ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800")
        const catIcon = cat === "photos" ? "image" : (cat === "videos" ? "videocam" : "description")

        html += `
          <div class="mb-4">
            <div class="flex items-center gap-2 mb-2">
              <span class="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${catBadgeColor}">
                <span class="material-symbols-outlined !text-xs">${catIcon}</span>
                ${catTitle} (${files.length})
              </span>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        `

        files.forEach((file, idx) => {
          if (cat === "photos") {
            const url = URL.createObjectURL(file)
            this.objectUrls.push(url)
            html += `
              <div class="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div class="flex items-center gap-2.5 min-w-0 flex-1">
                  <img src="${url}" alt="" class="w-10 h-10 rounded object-cover border border-gray-300 flex-shrink-0">
                  <div class="min-w-0 flex-1">
                    <p class="text-xs font-medium text-gray-900 truncate">${this.escapeHtml(file.name)}</p>
                    <p class="text-[11px] text-gray-500">${this.formatFileSize(file.size)}</p>
                  </div>
                </div>
                <button type="button"
                        data-action="click->asset-media-upload#removeStagedFile"
                        data-category="${cat}"
                        data-index="${idx}"
                        class="p-1 text-gray-400 hover:text-red-500 rounded hover:bg-gray-200 transition-colors"
                        title="${t("javascript.controllers.asset_media_upload_controller.remove")}">
                  <span class="material-symbols-outlined !text-base">close</span>
                </button>
              </div>
            `
          } else if (cat === "videos") {
            html += `
              <div class="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div class="flex items-center gap-2.5 min-w-0 flex-1">
                  <div class="w-10 h-10 rounded bg-purple-100 flex items-center justify-center text-purple-600 flex-shrink-0">
                    <span class="material-symbols-outlined !text-xl">videocam</span>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs font-medium text-gray-900 truncate">${this.escapeHtml(file.name)}</p>
                    <p class="text-[11px] text-gray-500">${this.formatFileSize(file.size)}</p>
                  </div>
                </div>
                <button type="button"
                        data-action="click->asset-media-upload#removeStagedFile"
                        data-category="${cat}"
                        data-index="${idx}"
                        class="p-1 text-gray-400 hover:text-red-500 rounded hover:bg-gray-200 transition-colors"
                        title="${t("javascript.controllers.asset_media_upload_controller.remove")}">
                  <span class="material-symbols-outlined !text-base">close</span>
                </button>
              </div>
            `
          } else {
            const icon = this.getDocumentIcon(file)
            html += `
              <div class="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div class="flex items-center gap-2.5 min-w-0 flex-1">
                  <div class="w-10 h-10 rounded bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
                    <span class="material-symbols-outlined !text-xl">${icon}</span>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs font-medium text-gray-900 truncate">${this.escapeHtml(file.name)}</p>
                    <p class="text-[11px] text-gray-500">${this.formatFileSize(file.size)}</p>
                  </div>
                </div>
                <button type="button"
                        data-action="click->asset-media-upload#removeStagedFile"
                        data-category="${cat}"
                        data-index="${idx}"
                        class="p-1 text-gray-400 hover:text-red-500 rounded hover:bg-gray-200 transition-colors"
                        title="${t("javascript.controllers.asset_media_upload_controller.remove")}">
                  <span class="material-symbols-outlined !text-base">close</span>
                </button>
              </div>
            `
          }
        })

        html += `
            </div>
          </div>
        `
      }
    })

    this.previewContainerTarget.innerHTML = html

    // Update empty notice and submit button
    if (this.hasEmptyNoticeTarget) {
      if (totalCount === 0) {
        this.emptyNoticeTarget.classList.remove("hidden")
      } else {
        this.emptyNoticeTarget.classList.add("hidden")
      }
    }

    if (this.hasSubmitBtnTarget) {
      if (totalCount > 0) {
        this.submitBtnTarget.disabled = false
        this.submitBtnTarget.classList.remove("opacity-50", "cursor-not-allowed")
        if (this.hasSubmitBtnTextTarget) {
          this.submitBtnTextTarget.textContent = t("javascript.controllers.asset_media_upload_controller.upload_n_files", { count: totalCount })
        }
      } else {
        this.submitBtnTarget.disabled = true
        this.submitBtnTarget.classList.add("opacity-50", "cursor-not-allowed")
        if (this.hasSubmitBtnTextTarget) {
          this.submitBtnTextTarget.textContent = t('javascript.controllers.asset_media_upload_controller.upload_files')
        }
      }
    }
  }

  handleSubmit(event) {
    const totalCount = Object.values(this.filesMap).reduce(
      (sum, dt) => sum + (dt?.files?.length || 0),
      0
    )

    if (totalCount === 0) {
      event.preventDefault()
      alert(t('javascript.controllers.asset_media_upload_controller.please_select_at_least_one_photo_document_or_video'))
      return
    }

    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.classList.add("opacity-75", "cursor-wait")
    }

    if (this.hasSubmitSpinnerTarget) {
      this.submitSpinnerTarget.classList.remove("hidden")
    }

    if (this.hasSubmitBtnTextTarget) {
      this.submitBtnTextTarget.textContent = t("javascript.controllers.asset_media_upload_controller.uploading_n_files", { count: totalCount })
    }
  }

  getCurrentInput() {
    return this.fileInputTargets.find(i => i.dataset.category === this.activeCategoryValue)
  }

  getDocumentIcon(file) {
    const type = (file.type || "").toLowerCase()
    const name = (file.name || "").toLowerCase()

    if (type === "application/pdf" || name.endsWith(".pdf")) return "picture_as_pdf"
    if (type.includes("word") || name.endsWith(".doc") || name.endsWith(".docx")) return "description"
    if (type.includes("sheet") || type.includes("excel") || name.endsWith(".xls") || name.endsWith(".xlsx") || name.endsWith(".csv")) return "table_chart"
    if (type.includes("zip") || type.includes("compressed") || name.endsWith(".zip") || name.endsWith(".rar")) return "folder_zip"
    return "insert_drive_file"
  }

  formatFileSize(bytes) {
    if (!bytes || bytes === 0) return "0 B"
    const k = 1024
    const sizes = ["B", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 10) / 10 + " " + sizes[i]
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  revokeObjectUrls() {
    if (this.objectUrls && this.objectUrls.length > 0) {
      this.objectUrls.forEach(url => URL.revokeObjectURL(url))
      this.objectUrls = []
    }
  }
}
